import { abbreviateDate } from '@utils/date';
import { sortStrings } from '@utils/sorting';

import { FileCellEntry, MultiSelectCellValue, SelectCellValue, UserCellValue } from './types';

import { isEmptyColumnValue } from '../table-utils';
import { ColumnEditValue, ColumnType } from '../types';

export type CellActionKey = 'copy' | 'edit' | 'sendEmail' | 'remove';

// The cell renders a menu of actions before it opens an editor, so every type
// needs to say what its value looks like as text, what clearing it sends to the
// API, and which of the actions make sense for it.

export const getCopyableValue = (type: ColumnType | undefined, value: any): string | undefined => {
  if (isEmptyColumnValue(value)) {
    return undefined;
  }

  switch (type) {
    case 'single_select': {
      const option = value as SelectCellValue | string;
      return typeof option === 'object' ? option.label : String(option);
    }
    case 'multi_select': {
      const options = value as MultiSelectCellValue;
      return sortStrings(options.map((option) => option.label)).join(', ');
    }
    case 'user': {
      const user = value as UserCellValue;
      return user.name || user.email || undefined;
    }
    // Copy hands over what the cell reads, for a date as much as for an option
    // label - the stored value is an ISO string, which is not what was on
    // screen to be copied.
    case 'date':
      return abbreviateDate(value, 'en');
    case 'datetime':
      return abbreviateDate(value, 'en', 'datetime');
    case 'file':
    case 'file_multi':
      return undefined;
    default:
      return typeof value === 'string' ? value : String(value);
  }
};

export const getEmptyEditValue = (type: ColumnType | undefined, value: any): ColumnEditValue => {
  switch (type) {
    case 'multi_select':
      return [];
    case 'file':
    case 'file_multi': {
      const files = (Array.isArray(value) ? value : []) as Array<FileCellEntry>;
      return {
        addedFiles: [],
        removedFileShortIds: files.map((file) => String(file.id)),
      };
    }
    default:
      return '';
  }
};

// Types whose editor is a list the user picks from, and which can be emptied
// from inside that list - a select through "No option", a user through "No
// user", a multi select by unticking, files through their own rows. The
// designs open all of them on the first click, so they get no action menu.
// Everything else keeps one, most of all the date: its calendar can only pick
// a day, so Remove is the only way to empty it again.
const DIRECT_EDIT_TYPES: ReadonlyArray<ColumnType> = [
  'single_select',
  'multi_select',
  'user',
  'file',
  'file_multi',
];

export const getMenuActionKeys = (
  type: ColumnType | undefined,
  value: any,
  { isClearable = true }: { isClearable?: boolean } = {},
): Array<CellActionKey> => {
  if (type && DIRECT_EDIT_TYPES.includes(type)) {
    return [];
  }

  const keys: Array<CellActionKey> = [];

  if (getCopyableValue(type, value) !== undefined) {
    keys.push('copy');
  }

  keys.push('edit');

  if (type === 'email') {
    keys.push('sendEmail');
  }

  if (isClearable) {
    keys.push('remove');
  }

  return keys;
};
